// @ts-check
// Formulários da identificação (contrato de bots, seções 4.8 a 4.10): identificar o responsável (pelo telefone; com
// telefone compartilhado, "Quem está falando?"; sem cadastro, CPF do responsável e nascimento do aluno), escolher o
// aluno (assunto, "Toda a família") e a verificação forte por código. Os textos vazios usam o padrão do motor,
// mostrado como exemplo no campo.
import { html } from 'htm/preact';
import { Toggle } from '../../../ui/feedback.js';
import { BOT_STRONG_VERIFICATION_PURPOSES } from '../../../../../shared/bots/bot-definition.js';
import { FlowNotice } from '../../flows/flow-ui.js';
import { BotSelectField, BotTextField } from '../bot-form-fields.js';
import { t } from '../../../i18n/index.js';

/** @typedef {import('../bot-node-panel.js').BotNodeFormProps} BotNodeFormProps */

/**
 * Linha com o interruptor e o texto.
 * @param {{ value: boolean, label: string, onChange: (value: boolean) => void }} props
 */
export function BotToggleRow({ value, label, onChange }) {
  return html`<label class="flow-checkbox-row bot-toggle-row">
    <${Toggle} value=${value} ariaLabel=${label} onChange=${onChange} />
    <span>${label}</span>
  </label>`;
}

/** @param {BotNodeFormProps} props */
export function IdentifyGuardianForm({ node, variables, onConfig }) {
  const config = node.config ?? {};
  const texts = config.texts ?? {};
  /** @param {string} key @param {string} value */
  const setText = (key, value) => onConfig({ texts: { ...texts, [key]: value || null } });
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-identify-form">
    <${FlowNotice}>${t('bots.forms.identify.notice')}<//>
    <${BotToggleRow} value=${config.askSpeakerWhenShared !== false} label=${t('bots.forms.identify.askSpeaker')}
      onChange=${(askSpeakerWhenShared) => onConfig({ askSpeakerWhenShared })} />
    <${BotToggleRow} value=${config.allowWeakData !== false} label=${t('bots.forms.identify.allowWeakData')}
      onChange=${(allowWeakData) => onConfig({ allowWeakData })} />
    <details class="bot-form-details">
      <summary>${t('bots.forms.texts')}</summary>
      <${BotTextField} label=${t('bots.forms.identify.askWho')} value=${texts.askWho} placeholder=${t('bots.defaultTexts.askWho')} variables=${variables}
        onChange=${(value) => setText('askWho', value)} />
      ${config.allowWeakData !== false
        ? html`<${BotTextField} label=${t('bots.forms.identify.askCpf')} value=${texts.askCpf} placeholder=${t('bots.defaultTexts.askCpf')} variables=${variables}
            onChange=${(value) => setText('askCpf', value)} />
          <${BotTextField} label=${t('bots.forms.identify.askBirthDate')} value=${texts.askBirthDate} placeholder=${t('bots.defaultTexts.askBirthDate')} variables=${variables}
            onChange=${(value) => setText('askBirthDate', value)} />
          <${BotTextField} label=${t('bots.forms.identify.notFound')} value=${texts.notFound} placeholder=${t('bots.defaultTexts.notFound')} variables=${variables}
            onChange=${(value) => setText('notFound', value)} />`
        : null}
    </details>
    <p class="flow-field-hint">${t('bots.forms.identify.branches')}</p>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function ChooseStudentForm({ node, variables, onConfig }) {
  const config = node.config ?? {};
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-choose-student-form">
    <${BotSelectField} label=${t('bots.forms.chooseStudent.topic')} value=${config.topic ?? 'GENERAL'} placeholder=${null}
      options=${['GENERAL', 'ADMINISTRATIVE', 'PEDAGOGICAL', 'FINANCIAL'].map((value) => ({ value, label: t(`bots.topics.${value}`) }))}
      onChange=${(topic) => onConfig({ topic })} hint=${t('bots.forms.chooseStudent.topicHint')} />
    <${BotToggleRow} value=${config.allowWholeFamily !== false} label=${t('bots.forms.chooseStudent.wholeFamily')}
      onChange=${(allowWholeFamily) => onConfig({ allowWholeFamily })} />
    <${BotTextField} label=${t('bots.forms.chooseStudent.text')} value=${config.text} placeholder=${t('bots.defaultTexts.chooseStudent')} variables=${variables}
      onChange=${(text) => onConfig({ text: text || null })} />
    <p class="flow-field-hint">${t('bots.forms.chooseStudent.hint')}</p>
  </div>`;
}

/** @param {BotNodeFormProps} props */
export function StrongVerificationForm({ node, variables, onConfig }) {
  const config = node.config ?? {};
  const texts = config.texts ?? {};
  /** @param {string} key @param {string} value */
  const setText = (key, value) => onConfig({ texts: { ...texts, [key]: value || null } });
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-strong-verification-form">
    <${BotSelectField} label=${t('bots.forms.strong.purpose')} value=${config.purpose ?? 'SENSITIVE_TOPIC'} placeholder=${null}
      options=${BOT_STRONG_VERIFICATION_PURPOSES.map((value) => ({ value, label: t(`bots.verificationPurposes.${value}`) }))}
      onChange=${(purpose) => onConfig({ purpose })} />
    <${FlowNotice}>${t('bots.forms.strong.notice')}<//>
    <details class="bot-form-details">
      <summary>${t('bots.forms.texts')}</summary>
      <${BotTextField} label=${t('bots.forms.strong.sent')} value=${texts.sent} placeholder=${t('bots.defaultTexts.codeSent')} variables=${variables}
        onChange=${(value) => setText('sent', value)} />
      <${BotTextField} label=${t('bots.forms.strong.invalid')} value=${texts.invalid} placeholder=${t('bots.defaultTexts.codeInvalid')} variables=${variables}
        onChange=${(value) => setText('invalid', value)} />
    </details>
  </div>`;
}
